<template>
  <div>
    <!-- Page Header Start -->
    <div class="page-title-header">
      <div class="page-header">
        <BreadCrumb
          parentRouteName="BookingPresetList"
          :extra-items="isEditing ? [
            {
              text: bookingPreset.name,
              to: 'BookingCenterBookingList',
              params: $route.params.id
            },
            '編輯預約設定檔'
          ] : [
            '新增預約設定檔'
          ]"
        />
      </div>
    </div>
    <!-- Page Header End -->

    <!-- Form Start -->
    <div class="email-wrapper wrapper">
      <div class="row bg-white">
        <div class="col-md-2 mail-sidebar pt-3">
          <StepNavigation
            ref="stepNavigation"
            :steps="navigationSteps"
            @changed="newStep => step = newStep.hash"
          />
        </div>
        <div class="col-md-10 main-view">
          <b-overlay :show="showLoading">
            <b-card>
              <!-- 基本資料 section start -->
              <section v-if="step == ''">
                <!-- 欄位部分 Start -->
                <div class="row d-flex mb-4 mb-xl-2">
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    {{ isEditing ? "編輯預約設定檔" : "新增預約設定檔" }}
                  </h4>
                  <div class="col-12 col-xl-8">
                    <validation-error-alert
                      v-if="validationErrors"
                      :errors="validationErrors"
                    ></validation-error-alert>
                    <!-- 名稱 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="*預約設定檔名稱"
                    >
                      <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        type="text"
                        :state="v$.bookingPreset.name.$error ? false : null"
                        v-model="bookingPreset.name"
                      ></b-form-input>
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.name.$error"
                      >
                        此欄位為必填
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 名稱 End -->
                    <!-- 類型 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="*預約類型"
                    >
                      <b-form-radio-group
                        v-if="hasBookingPresetTypePermission"
                        v-model="bookingPreset.type"
                        :options="bookingPresetTypes"
                      ></b-form-radio-group>
                      <p v-else class="mt-1">
                        無權限
                      </p>
                    </b-form-group>
                    <!-- 類型 End -->
                    <!-- 填寫權限 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="填寫權限"
                    >
                      <b-form-checkbox-group
                        v-model="bookingPreset.config.permissions"
                        :options="permissionOptions"
                      ></b-form-checkbox-group>
                    </b-form-group>
                    <!-- 填寫次數 Start -->
                    <!-- 描述 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約設定檔說明"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.description"
                      ></b-form-textarea>
                    </b-form-group>
                    <!-- 描述 End -->                    
                    <!-- 瀏覽器紀錄 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="是否開放預約"
                      description="是否允許使用者新增預約"
                    >
                      <b-form-select
                        v-model="bookingPreset.status"
                      >
                        <b-form-select-option value="published">發布</b-form-select-option>
                        <b-form-select-option value="draft">草稿</b-form-select-option>
                        <b-form-select-option value="closed">關閉</b-form-select-option>
                      </b-form-select>
                    </b-form-group>
                    <!-- 瀏覽器紀錄 End -->
                    <!-- 表單開始時間 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="* 開始時間"
                    >
                      <AppDateTimePicker
                        v-model="bookingPreset.start_at"
                        placeholderForDate="選擇日期"
                        placeholderForTime="選擇時間"
                        :has-error="v$.bookingPreset.start_at.$error"
                      />
                      <b-form-invalid-feedback :state="!v$.bookingPreset.start_at.$error">
                        此欄位為必填
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 表單開始時間 End -->
                    <!-- 表單結束時間 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="* 結束時間"
                    >
                      <AppDateTimePicker
                        v-model="bookingPreset.end_at"
                        placeholderForDate="選擇日期"
                        placeholderForTime="選擇時間"
                        :has-error="v$.bookingPreset.end_at.$error || v$.bookingPreset.start_at.beforeEndAt.$invalid"
                      />
                      <b-form-invalid-feedback :state="!v$.bookingPreset.end_at.$error">
                        此欄位為必填
                      </b-form-invalid-feedback>
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.start_at.beforeEndAt.$invalid"
                      >
                        結束時間必須大於起始時間
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 表單結束時間 End -->
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button :disabled="!hasBookingPresetTypePermission" @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- 基本資料 section end -->

              <!-- 歡迎頁 -->
              <section v-if="step == '#welcome' && bookingPreset.config.welcome_page">
                <h4 class="mb-3 font-weight-bold">歡迎頁</h4>
                <validation-error-alert
                  v-if="validationErrors"
                  :errors="validationErrors"
                ></validation-error-alert>
                <!-- 啟用 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="啟用"
                >
                  <b-form-checkbox v-model="bookingPreset.config.welcome_page.enable">啟用</b-form-checkbox>
                </b-form-group>
                <!-- 啟用 End -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                >
                  <template #label>
                    主圖
                    <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                  </template>
                  <template #default>
                    <AppFileUploader
                      v-model="bookingPreset.config.welcome_page.banner_image"
                      placeholder-when-uploaded="（查看檔案）"
                      placeholder-when-empty=""
                    />
                  </template>
                </b-form-group>
                <!-- 標題 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="標題"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    :disabled="!bookingPreset.config.welcome_page.enable"
                    v-model="bookingPreset.config.welcome_page.form_title"
                  ></b-form-input>
                </b-form-group>
                <!-- 標題 End -->
                <!-- 副標題 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="副標題"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    :disabled="!bookingPreset.config.welcome_page.enable"
                    v-model="bookingPreset.config.welcome_page.form_desc"
                  ></b-form-input>
                </b-form-group>
                <!-- 副標題 End -->
                <!-- 內容說明 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="內容說明"
                >
                  <TrixEditor :readonly="!bookingPreset.config.welcome_page.enable" v-model="bookingPreset.config.welcome_page.content" />
                </b-form-group>
                <!-- 內容說明 End -->
                <!-- 按鈕文字 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="按鈕文字"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    :disabled="!bookingPreset.config.welcome_page.enable"
                    v-model="bookingPreset.config.welcome_page.button_text"
                  ></b-form-input>
                </b-form-group>
                <!-- 按鈕文字 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button
                    @click="submit"
                    variant="success"
                  >
                    儲存
                  </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>

              <!-- 內容頁 -->
              <section v-if="step == '#content' && bookingPreset.config.content_page">
                <h4 class="mb-3 font-weight-bold">內容頁</h4>
                <validation-error-alert
                  v-if="validationErrors"
                  :errors="validationErrors"
                ></validation-error-alert>
                <!-- 每頁都顯示 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="每頁都顯示"
                >
                  <b-form-checkbox v-model="bookingPreset.config.content_page.displayed_in_every_page">每頁都顯示</b-form-checkbox>
                </b-form-group>
                <!-- 每頁都顯示 End -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                >
                  <template #label>
                    主圖
                    <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                  </template>
                  <template #default>
                    <AppFileUploader
                      v-model="bookingPreset.config.content_page.banner_image"
                      placeholder-when-uploaded="（查看檔案）"
                      placeholder-when-empty=""
                    />
                  </template>
                </b-form-group>
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                >
                  <template #label>
                    背景底圖
                    <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                  </template>
                  <template #default>
                    <AppFileUploader
                      v-model="bookingPreset.config.content_page.background_image"
                      placeholder-when-uploaded="（查看檔案）"
                      placeholder-when-empty=""
                    />
                  </template>
                </b-form-group>
                <!-- 標題 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="標題"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    v-model="bookingPreset.config.content_page.form_title"
                  ></b-form-input>
                </b-form-group>
                <!-- 標題 End -->
                <!-- 副標題 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="副標題"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    v-model="bookingPreset.config.content_page.form_desc"
                  ></b-form-input>
                </b-form-group>
                <!-- 副標題 End -->
                <!-- 內容說明 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="內容說明"
                >
                  <TrixEditor v-model="bookingPreset.config.content_page.content"/>
                </b-form-group>
                <!-- 內容說明 End -->
                <!-- 送出按鈕文字 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="3"
                  label-size="sm"
                  label="送出按鈕文字"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    v-model="bookingPreset.config.content_page.submit_button_text"
                  ></b-form-input>
                </b-form-group>
                <!-- 送出按鈕文字 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button
                    @click="submit"
                    variant="success"
                  >
                    儲存
                  </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>

              <!-- 完成頁 -->
              <section v-if="step == '#finished' && bookingPreset.config.finished_page">
                <div class="row d-flex mb-4 mb-xl-2">
                  <h4 class="col-12 col-xl-12 mb-3 font-weight-bold">完成頁</h4>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">一般設定</h5>
                  <div class="col-12 col-xl-12">
                    <!-- 標題 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="標題"
                    >
                      <b-form-input
                        v-model="bookingPreset.config.finished_page.title"
                        placeholder="請輸入標題"
                      ></b-form-input>
                    </b-form-group>
                    <!-- 標題 End -->

                    <!-- 內容說明 Start -->
                    <div class="form-group row">
                      <label class="col-12 col-lg-2 col-form-label col-form-label-sm">內容說明</label>
                      <div class="col-12 col-lg-10">
                        <TrixEditor v-model="bookingPreset.config.finished_page.content"/>
                      </div>
                    </div>
                    <!-- 內容說明 End -->

                    <!-- 主圖 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <template #label>
                        主圖
                        <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                      </template>
                      <template #default>
                        <AppFileUploader
                          v-model="bookingPreset.config.finished_page.banner_image"
                          placeholder-when-uploaded="（查看檔案）"
                          placeholder-when-empty=""
                        />
                      </template>
                    </b-form-group>
                    <!-- 主圖 End -->
                  </div>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">按鈕設定</h5>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="顯示繼續預約按鈕"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.finished_page.enabled_next_booking_button"
                      ></b-form-checkbox>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="顯示預約紀錄按鈕"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.finished_page.enabled_go_booking_list_button"
                      ></b-form-checkbox>
                    </b-form-group>
                  </div>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">訊息發送</h5>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用預約後訊息"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.finished_page.send_messages.enabled"
                      ></b-form-checkbox>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="訊息內容"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.finished_page.send_messages.text"
                        rows="4"
                      ></b-form-textarea>
                    </b-form-group>
                  </div>

                  <hr class="col-12" />

                  <div class="col-12 col-xl-8 mb-4">
                    <h5 class="mb-2 font-weight-bold">預約人</h5>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="標題"
                    >
                      <b-form-input
                        v-model="bookingPreset.config.finished_page.booking_person_title"
                        placeholder="請輸入預約人標題"
                      ></b-form-input>
                    </b-form-group>

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用顯示客製欄位"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.finished_page.enabled_booking_person_custom_fields"
                      ></b-form-checkbox>
                    </b-form-group>


                    <JsonEditor
                      :lines="4"
                      v-model="bookingPreset.config.finished_page.booking_person"
                    />
                  </div>
                  <div class="col-12 col-xl-8 mb-4">
                    <h5 class="mb-2 font-weight-bold">預約資訊</h5>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="標題"
                    >
                      <b-form-input
                        v-model="bookingPreset.config.finished_page.booking_info_title"
                        placeholder="請輸入預約資訊標題"
                      ></b-form-input>
                    </b-form-group>

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用顯示客製欄位"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.finished_page.enabled_booking_info_custom_fields"
                      ></b-form-checkbox>
                    </b-form-group>
                    <JsonEditor
                      :lines="4"
                      v-model="bookingPreset.config.finished_page.booking_info"
                    />
                  </div>
                  <div class="col-12 col-xl-8">
                    <h5 class="mb-2 font-weight-bold">表單內容</h5>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="標題"
                    >
                      <b-form-input
                        v-model="bookingPreset.config.finished_page.form_content_title"
                        placeholder="請輸入表單內容標題"
                      ></b-form-input>
                    </b-form-group>

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用顯示客製欄位"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.finished_page.enabled_form_content_custom_fields"
                      ></b-form-checkbox>
                    </b-form-group>
                    <JsonEditor
                      :lines="4"
                      v-model="bookingPreset.config.finished_page.form_content"
                    />
                  </div>
                </div>

                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button
                    @click="submit"
                    variant="success"
                  >
                    儲存
                  </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>

              <!-- 欄位設定 section start -->
              <section v-if="step == '#fields'">
                <NameBlock :label="'預約名稱'" :name="bookingPreset.name" />
                <div>
                  <DynamicFormEditor
                    v-if="bookingPreset.id"
                    @output="saveFields"
                    @cancel="$router.push({ name: 'BookingPresetList' })"
                    @change="handleFieldChange"
                    editor-title="欄位設定"
                    :form-type="'booking'"
                    :input-subjects="bookingPreset.config.fields"
                    :pages="bookingPreset.config.pages"
                    :sections="bookingPreset.config.sections"
                    :validate-subject="validSubject"
                    :booking-preset-type="bookingPreset.type"
                    :start-at-field-id="dataModel.startAtFieldId"
                    validation-message="標題不得為空"
                    enable-visible-condition
                  ></DynamicFormEditor>
                </div>
              </section>
              <!-- 欄位設定 section end -->

              <!-- 預約資料設定 section start -->
              <section v-if="step == '#mapping'">
                <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                <div class="row d-flex mb-4 mb-xl-2">
                  <h4 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    預約資料設定
                  </h4>
                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    欄位對應
                  </h5>
                  <div class="col-12 col-xl-9">
                    <!-- 標題 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="標題顯示欄位"
                    >
                      <b-form-select
                        v-model="
                          bookingPreset.config.booking_mapping.bookings.title
                        "
                        :options="titleOptions"
                      ></b-form-select>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="標題顯示模板"
                    >
                      <b-form-textarea
                        v-model="
                          bookingPreset.config.booking_mapping.bookings.title_template
                        "
                        rows="3"
                        max-rows="6"
                        :placeholder="`{{$booking_data['欄位ID']}} - {{$customer['extra']['name']}} : {{$booking_data['欄位ID']}}`"
                      ></b-form-textarea>
                    </b-form-group>
                    <!-- 標題 End -->
                    <!-- 預約開始欄位 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約開始欄位"
                    >
                      <b-form-select
                        v-model="
                          bookingPreset.config.booking_mapping.bookings.start_at
                        "
                        :options="dateFieldCandidates"
                      ></b-form-select>
                    </b-form-group>
                    <!-- 預約開始欄位 End -->
                    <!-- 預約結束欄位 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約結束欄位"
                    >
                      <b-form-select
                        v-model="
                          bookingPreset.config.booking_mapping.bookings.end_at
                        "
                        :options="dateFieldCandidates"
                        :disabled="bookingPreset.type == 'wizard'"
                      ></b-form-select>
                    </b-form-group>
                    <!-- 預約結束欄位 End -->
                    <!-- 預留時間區段(分鐘) Start-->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <template #label>
                        <b-tooltip target="end-slot-minutes" placement="top">
                          在結束時間之後加上一個固定時間
                        </b-tooltip>
                        <span id="end-slot-minutes">預留時間區段(分鐘)<span class="mdi mdi-help-circle-outline"></span></span>
                      </template>
                      <b-form-input
                        v-model="bookingPreset.config.booking_mapping.bookings.end_slot_minutes"
                        placeholder="請填入分鐘"
                        type="number"
                        min="0"
                        ref="endSlotMinutes"
                        @blur="sanitizeEndSlotMinutes"
                      ></b-form-input>
                      <b-form-invalid-feedback
                        :state="
                          !v$.bookingPreset.config.booking_mapping.bookings.end_slot_minutes.$error
                        "
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.booking_mapping.bookings.end_slot_minutes"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 預留時間區段(分鐘) End-->
                    <!-- 前台不顯示結束時間 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="前台不顯示結束時間"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.display.should_hide_end_time"
                      >
                      </b-form-checkbox>
                    </b-form-group>
                    <!-- 前台不顯示結束時間 End -->
                    <!-- 預約開始條件 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約開始條件(分店)"
                    >
                      <b-form-select
                        v-model="
                          bookingPreset.config.booking_mapping.bookings.start_at_by_branch
                        "
                        :options="branchFieldCandidates"
                        :disabled="!bookingPreset.config.booking_mapping.bookings.start_at"
                      ></b-form-select>
                    </b-form-group>
                    <!-- 預約開始條件 End -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <template #label>
                        <b-tooltip target="closing-time" placement="top">
                          店家當日休店時段，被預約的服務不會晚於此時段
                        </b-tooltip>
                        <span id="closing-time">*當日休店時間<span class="mdi mdi-help-circle-outline"></span></span>
                      </template>
                      <AppTimePicker
                        styleType="unlimited"
                        v-model="bookingPreset.config.booking_mapping.bookings.closing_time"
                        ref="closingTime"
                      />
                      <b-form-invalid-feedback
                        :state="
                          !v$.bookingPreset.config.booking_mapping.bookings.closing_time.$error
                        "
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.booking_mapping.bookings.closing_time"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <!-- 提前預約設定 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="提前預約設定"
                    >
                      <label class="py-2 d-inline-flex align-items-center">
                        <b-form-radio name="lead-time-options" v-model="bookingPreset.config.basic.lead_time.type" value="none" />
                        無需提前預約
                      </label>
                      <br/>
                      <label class="py-2 d-inline-flex align-items-center">
                        <b-form-radio name="lead-time-options" v-model="bookingPreset.config.basic.lead_time.type" value="day" />
                        需提前
                        <b-form-input
                          :disabled="bookingPreset.config.basic.lead_time.type !== 'day'"
                          v-model="bookingPreset.config.basic.lead_time.amount_day"
                          type="number"
                          min="0"
                          class="d-inline-block mx-1"
                          style="width: 70px;"
                          size="sm"
                        ></b-form-input>
                        天預約
                      </label>
                      <br/>
                      <label class="py-2 d-inline-flex align-items-center">
                        <b-form-radio name="lead-time-options" v-model="bookingPreset.config.basic.lead_time.type" value="minute" />
                        需提前
                        <b-form-input
                          :disabled="bookingPreset.config.basic.lead_time.type !== 'minute'"
                          v-model="bookingPreset.config.basic.lead_time.amount_minute"
                          type="number"
                          min="0"
                          class="d-inline-block mx-1"
                          style="width: 70px;"
                          size="sm"
                        ></b-form-input>
                        分鐘預約
                      </label>
                    </b-form-group>
                    <!-- 提前預約設定 End -->

                    <!-- 預約時間限制 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="*預約時間限制"
                    >
                      <label class="py-2 d-inline-flex align-items-center">
                        <b-form-radio name="booking-window-options" v-model="bookingPreset.config.basic.booking_window.type" value="none" />
                        不限制
                      </label>
                      <br/>
                      <label class="py-2 d-inline-flex align-items-center">
                        <b-form-radio name="booking-window-options" v-model="bookingPreset.config.basic.booking_window.type" value="day" />
                        只能預約
                        <b-form-input
                          :disabled="bookingPreset.config.basic.booking_window.type !== 'day'"
                          v-model="bookingPreset.config.basic.booking_window.amount_day"
                          type="number"
                          min="0"
                          class="d-inline-block mx-1"
                          style="width: 70px;"
                          size="sm"
                        ></b-form-input>
                        天內的預約
                        <span id="booking-window-tooltip-trigger" class="ml-2"><span class="mdi mdi-help-circle-outline"></span></span>
                        <b-tooltip target="booking-window-tooltip-trigger" placement="top">
                          1 天代表當日；2 代表當日與隔日
                        </b-tooltip>
                      </label>
                      <br/>
                      <label class="py-2 d-inline-flex align-items-center">
                        <b-form-radio name="booking-window-options" v-model="bookingPreset.config.basic.booking_window.type" value="minute" />
                        只能預約未來
                        <b-form-input
                          :disabled="bookingPreset.config.basic.booking_window.type !== 'minute'"
                          v-model="bookingPreset.config.basic.booking_window.amount_minute"
                          type="number"
                          min="0"
                          class="d-inline-block mx-1"
                          style="width: 70px;"
                          size="sm"
                        ></b-form-input>
                        分鐘內的預約
                      </label>
                    </b-form-group>
                    <!-- 預約時間限制 End -->

                    <!-- 預約提示 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示"
                    >
                      <TrixEditor v-model="bookingPreset.config.basic.tips" />
                    </b-form-group>
                    <!-- 預約提示 End -->

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="分店欄位"
                    >
                      <b-form-select
                        v-model="
                          bookingPreset.config.branch.field_id
                        "
                        :options="branchFieldCandidates"
                      ></b-form-select>
                    </b-form-group>
                  </div>

                  <!-- 手機驗證設定 Start -->
                  <h5 class="col-12 col-xl-12 mt-5 mb-3 font-weight-bold">
                    手機驗證
                  </h5>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="驗證時機"
                    >
                      <b-form-select
                        v-model="bookingPreset.config.phone_verification.timing"
                        :options="[{ text: '無須驗證', value: null }, { text: '填寫表單時驗證', value: 'current_page' }, { text: '填寫後下頁驗證(尚未開放此功能)', value: 'next_page' }]"
                      ></b-form-select>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="手機驗證欄位"
                    >
                      <b-form-select
                        v-model="bookingPreset.config.phone_verification.field_id"
                        :options="textFieldCandidates"
                        :disabled="!bookingPreset.config.phone_verification.timing"
                      ></b-form-select>
                      <b-form-invalid-feedback
                        v-if="bookingPreset.config.phone_verification.timing"
                        :state="!(bookingPreset.config.phone_verification.timing && !bookingPreset.config.phone_verification.field_id)"
                      >
                        請選擇需要驗證的欄位
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>
                  <!-- 手機驗證設定 End -->

                  <h5 class="col-12 col-xl-12 mt-5 mb-3 font-weight-bold">
                    預約狀態
                  </h5>
                  <div class="col-12 col-xl-8">
                    <!-- 預約狀態欄位 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="狀態欄位"
                    >
                      <b-form-select
                        v-model="
                          bookingPreset.config.booking_mapping.bookings.status
                        "
                        :options="collectionFieldCandidates"
                      ></b-form-select>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="狀態預設值"
                    >
                      <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        type="text"
                        v-model="
                          bookingPreset.config.booking_mapping
                            .default_status_value
                        "
                      ></b-form-input>
                    </b-form-group>
                    <!-- 預約狀態對應 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="狀態文字對應"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="
                          bookingPreset.config.provider_config.status_filter_mapping
                        "
                        ref="statusFilterMapping"
                      />
                      <b-form-invalid-feedback
                        :state="
                          !v$.bookingPreset.config.provider_config.status_filter_mapping.$error
                        "
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.provider_config.status_filter_mapping"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 預約狀態對應 End -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="狀態顯示編輯按鈕"
                    >
                      <JsonEditor
                        height="64px"
                        v-model="bookingPreset.config.can_edit_at_liff_mapping"
                        ref="editLiffMapping"
                      />
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.config.can_edit_at_liff_mapping.$error"
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.can_edit_at_liff_mapping"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 預約狀態欄位 End -->
                  </div>
                  <h5 class="col-12 col-xl-12 mt-5 mb-3 font-weight-bold">
                    狀態自動變更
                  </h5>
                  <div class="col-12 col-xl-8">
                    <!-- 啟動狀態自動變更 start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <b-form-checkbox v-model="bookingPreset.config.auto_change_status_enabled">啟動狀態自動變更</b-form-checkbox>
                    </b-form-group>
                    <!-- 啟動狀態自動變更 end -->
                    <!-- 變更日期欄位 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="變更日期欄位"
                    >
                      <b-form-select
                        v-model="bookingPreset.config.auto_change_status_date_field_id"
                        :state="v$.bookingPreset.config.auto_change_status_date_field_id.$error ? false : null"
                        :options="dateFieldCandidates"
                      />
                      <b-form-invalid-feedback
                        v-if="v$.bookingPreset.config.auto_change_status_date_field_id.$error"
                      >
                        {{ presentValidationErrors('bookingPreset.config.auto_change_status_date_field_id') }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 變更日期欄位 end -->
                    <!-- 變更狀態設定 start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="日期當天變更狀態設定"
                    >
                      <JsonEditor
                        height="150px"
                        v-model="bookingPreset.config.auto_change_status_equal"
                        ref="autoChangeStatusEqual"
                      />
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.config.auto_change_status_equal.$error"
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.auto_change_status_equal"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 變更狀態設定 end -->
                    <!-- 變更狀態設定 start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="超過日期變更狀態設定"
                    >
                      <JsonEditor
                        height="150px"
                        v-model="bookingPreset.config.auto_change_status_overdue"
                        ref="autoChangeStatusOverdue"
                      />
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.config.auto_change_status_overdue.$error"
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.auto_change_status_overdue"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 變更狀態設定 end -->
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- 預約資料設定 section end -->

              <!-- 精靈模式設定 section start -->
              <section v-if="step == '#wizard-mode'">
                <div class="row d-flex mb-4 mb-xl-2">
                  <div class="col-12 col-xl-12">
                    <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                  </div>
                  <h4 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    精靈模式設定
                  </h4>
                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    分店設定
                  </h5>
                  <div class="col-12 col-xl-8">
                    <!-- 分店區 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用選擇分店"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.enabled_selected_branch"
                      ></b-form-checkbox>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="顯示分店基本資料"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.display_branch_detail"
                      ></b-form-checkbox>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="基本資料明細"
                    >
                      <JsonEditor
                        :lines="5"
                        v-model="bookingPreset.config.branch_detail_mapping"
                      />
                    </b-form-group>
                    <!-- 基本資料欄位 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="基本資料欄位"
                    >
                      <b-form-radio-group                        
                        v-model="bookingPreset.config.branch_detail_expended"
                        :options="[{ text: '展開', value: true }, { text:'折疊', value: false }]"
                      ></b-form-radio-group>                      
                    </b-form-group>
                    <!-- 基本資料欄位 End -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="顯示分店圖片"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.display_branch_images"
                      ></b-form-checkbox>
                    </b-form-group>

                    <!-- 分店區 End -->
                    <hr />
                  </div>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    員工設定
                  </h5>
                  <div class="col-12 col-xl-8">
                    <!-- 員工區 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用選擇員工"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.enabled_selected_staff"
                      ></b-form-checkbox>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="顯示角色過濾"
                    >
                      <JsonEditor
                        height="32px"
                        v-model="bookingPreset.config.staff_role_filter"
                        :disabled="!bookingPreset.config.enabled_selected_staff"
                      />
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用員工班表設定"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.enabled_selected_staff_shift"
                      ></b-form-checkbox>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用員工服務關聯"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.enabled_staff_service_relation"
                      ></b-form-checkbox>
                    </b-form-group>
                    <!-- 員工區 End -->
                    <hr />
                  </div>
                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    服務設定
                  </h5>
                  <div class="col-12 col-xl-8">
                    <!-- 服務區 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="服務項目欄位"
                    >
                      <b-form-select
                        v-model="bookingPreset.config.service.service_field_id"
                        :options="serviceFieldCandidates"
                        @change="changeService"
                      ></b-form-select>
                    </b-form-group>
                    <!-- 精靈模式 service特殊設定欄位 -->
                    <div v-if="bookingPreset.config.service.collection_id">
                      <b-form-group
                        label-cols="12"
                        label-cols-lg="2"
                        label-size="sm"
                        label="圖片欄位"
                      >
                        <b-form-select
                          v-model="bookingPreset.config.service.image_item_id"
                          :disabled="!bookingPreset.config.service.service_field_id"
                          :options="collectionItemOptions(bookingPreset.config.service.collection_id)"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="12"
                        label-cols-lg="2"
                        label-size="sm"
                        label="標題欄位"
                      >
                        <b-form-select
                          v-model="bookingPreset.config.service.title_item_id"
                          :disabled="!bookingPreset.config.service.collection_id"
                          :state="!v$.bookingPreset.config.service.title_item_id.$error"
                          :options="collectionItemOptions(bookingPreset.config.service.collection_id)"
                        >
                        </b-form-select>
                        <b-form-invalid-feedback :state="!v$.bookingPreset.config.service.title_item_id.$error">
                          {{ presentValidationErrors('bookingPreset.config.service.title_item_id') }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                      <b-form-group
                        label-cols="12"
                        label-cols-lg="2"
                        label-size="sm"
                        label="敘述欄位"
                      >
                        <b-form-select
                          v-model="
                            bookingPreset.config.service.description_item_id
                          "
                          :disabled="!bookingPreset.config.service.collection_id"
                          :options="collectionItemOptions(bookingPreset.config.service.collection_id)"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="12"
                        label-cols-lg="2"
                        label-size="sm"
                        label="價格欄位"
                      >
                        <b-form-select
                          v-model="bookingPreset.config.service.price_item_id"
                          :disabled="!bookingPreset.config.service.collection_id"
                          :options="collectionItemOptions(bookingPreset.config.service.collection_id)"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="12"
                        label-cols-lg="2"
                        label-size="sm"
                        label="時間欄位"
                      >
                        <b-form-select
                          v-model="
                            bookingPreset.config.service.duration_item_id
                          "
                          :disabled="!bookingPreset.config.service.collection_id"
                          :options="collectionItemOptions(bookingPreset.config.service.collection_id)"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="12"
                        label-cols-lg="2"
                        label-size="sm"
                        label="允許多選服務"
                      >
                        <b-form-checkbox
                          v-model="bookingPreset.config.service.can_multiple_select"
                          :disabled="!bookingPreset.config.service.collection_id"
                        >
                        </b-form-checkbox>
                      </b-form-group>
                      <b-form-group
                        label-cols="12"
                        label-cols-lg="2"
                        label-size="sm"
                        label="單一服務時自動選擇"
                      >
                        <b-form-checkbox
                          v-model="bookingPreset.config.service.should_auto_select_the_only_option"
                          :disabled="!bookingPreset.config.service.collection_id"
                        >
                        </b-form-checkbox>
                      </b-form-group>
                      <!-- 服務區 End -->
                    </div>
                    <hr />
                    <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                      流程設定
                    </h5>
                    <div class="col-12 col-xl-8">
                      <b-form-group
                        label-cols="auto"
                        label-size="sm"
                        label="流程順序 - 分店"
                      >
                        <b-form-select
                          class="ml-4"
                          v-model="bookingPreset.config.order.branch_page"
                          :options="orderOptions"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="auto"
                        label-size="sm"
                        label="流程順序 - 員工"
                      >
                        <b-form-select
                          class="ml-4"
                          v-model="bookingPreset.config.order.staff_page"
                          :options="orderOptions"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="auto"
                        label-size="sm"
                        label="流程順序 - 服務"
                      >
                        <b-form-select
                          class="ml-4"
                          v-model="bookingPreset.config.order.service_page"
                          :options="orderOptions"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="auto"
                        label-size="sm"
                        label="流程順序 - 時間"
                      >
                        <b-form-select
                          class="ml-4"
                          v-model="bookingPreset.config.order.schedule_page"
                          :options="orderOptions"
                        >
                        </b-form-select>
                      </b-form-group>
                      <b-form-group
                        label-cols="auto"
                        label-size="sm"
                        label="流程順序 - 表單"
                      >
                        <b-form-select
                          class="ml-4"
                          v-model="bookingPreset.config.order.form_page"
                          :options="orderOptions"
                        >
                        </b-form-select>
                      </b-form-group>
                    </div>
                    <!-- 精靈模式 service特殊設定欄位 -->
                    <hr />
                    <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                      預約確認頁
                    </h5>
                    <div class="col-12 col-xl-8">
                      <b-form-group
                        label-cols="auto"
                        label-size="sm"
                        label="是否啟用"
                      >
                        <b-form-checkbox
                          v-model="bookingPreset.config.confirm_page.enable"
                        />
                      </b-form-group>
                    </div>
                    <hr />
                    <div class="col-12 col-xl-8">
                      <b-form-group
                        label-cols="auto"
                        label-size="sm"
                        label="送出預約按鈕文字"
                      >
                        <b-form-input
                          type="text"
                          v-model="bookingPreset.config.submit_button_text"
                        ></b-form-input>
                      </b-form-group>
                    </div>
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- 精靈模式設定 section end -->

              <!-- 精靈時間選擇頁 section start -->
              <section v-if="step == '#wizard-page'">
                <h4 class="mb-3 font-weight-bold">精靈時間選擇頁</h4>
                <div class="col-12 col-xl-8">
                  <!-- 預約提示標題 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="頁面標題"
                  >
                    <b-form-input
                      class="mb-2 mr-sm-2 mb-sm-0"
                      type="text"
                      v-model="bookingPreset.config.schedule_page.title"
                    ></b-form-input>
                  </b-form-group>
                  <!-- 預約提示標題 End -->
                  <hr/>
                </div>
                <div class="col-12 col-xl-8">
                <!-- 預約提示標題 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示標題"
                    >
                      <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        type="text"

                        v-model="bookingPreset.config.booking_tip.form_title"
                      ></b-form-input>
                    </b-form-group>
                    <!-- 預約提示標題 End -->
                    <!-- 預約提示說明 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示說明"
                    >
                    <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.booking_tip.form_desc"
                      ></b-form-textarea>
                    </b-form-group>
                    <!-- 預約提示說明 End -->
                     <!-- 啟用 send_messages Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="啟用 send_messages"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.booking_tip.send_messages.enabled"
                      ></b-form-checkbox>
                    </b-form-group>
                    <!-- 啟用 send_messages End -->
                     <!-- send_messages 內容 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="send_messages 內容"
                    >
                    <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.booking_tip.send_messages.text"
                      ></b-form-textarea>
                    </b-form-group>
                    <!-- send_messages 內容 End -->

                    <hr />

                   <!-- 預約提示上方按鈕啟用 Start -->
                   <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示上方按鈕啟用"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.booking_tip.button_top.enabled"
                      ></b-form-checkbox>
                    </b-form-group>
                    <!-- 預約提示上方按鈕啟用 End -->
                    <!-- 預約提示上方按鈕文字 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示上方按鈕文字"
                    >
                    <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.booking_tip.button_top.button_text"
                      ></b-form-input>
                    </b-form-group>
                    <!-- 預約提示上方按鈕文字 End -->
                    <!-- 預約提示上方按鈕動作 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示上方按鈕行為"
                    >
                      <b-form-select
                        v-model="bookingPreset.config.booking_tip.button_top.button_action"
                        :options="['OA', 'URL','Exit']"
                      >
                      </b-form-select>
                    </b-form-group>
                    <!-- 預約提示上方按鈕動作 End -->
                      <!-- 預約提示上方按鈕網址 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示上方按鈕網址"
                    >
                    <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.booking_tip.button_top.button_url"
                      ></b-form-input>
                    </b-form-group>
                    <!-- 預約提示上方按鈕網址 End -->


                    <hr />

                    <!-- 預約提示下方按鈕啟用 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示下方按鈕啟用"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.booking_tip.button_bottom.enabled"
                      ></b-form-checkbox>
                    </b-form-group>
                    <!-- 預約提示下方按鈕啟用 End -->
                    <!-- 預約提示下方按鈕文字 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示下方按鈕文字"
                    >
                    <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.booking_tip.button_bottom.button_text"
                      ></b-form-input>
                    </b-form-group>
                    <!-- 預約提示下方按鈕文字 End -->
                    <!-- 預約提示下方按鈕行為 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示下方按鈕行為"
                    >
                      <b-form-select
                        v-model="bookingPreset.config.booking_tip.button_bottom.button_action"
                        :options="['OA', 'URL','Exit']"
                      >
                      </b-form-select>
                    </b-form-group>
                    <!-- 預約提示下方按鈕行為 End -->
                      <!-- 預約提示下方按鈕網址 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約提示下方按鈕網址"
                    >
                    <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.booking_tip.button_bottom.button_url"
                      ></b-form-input>
                    </b-form-group>
                    <!-- 預約提示下方按鈕網址 End -->

                    <!-- 表單底部 Start -->
                    <div class="d-flex justify-content-center">
                      <b-button
                        class="mr-3"
                        @click="cancel"
                        variant="outline-danger"
                      >
                        返回
                      </b-button>
                      <b-button
                        @click="submit"
                        variant="success"
                      >
                        儲存
                      </b-button>
                    </div>
                    <!-- 表單底部 End -->
                  </div>
              </section>
              <!-- 精靈時間選擇頁 section end -->

              <!-- liff預約紀錄 section start -->
              <section v-if="step == '#liff-setting'">
                <!-- 欄位部分 Start -->
                <div class="row d-flex mb-4 mb-xl-2">
                  <div class="col-12 col-xl-12">
                    <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                  </div>
                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    LIFF 預約紀錄
                  </h5>
                  <div class="col-12 col-xl-8">
                    <!-- 預約記錄頁籤 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約記錄頁籤"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="
                          bookingPreset.config.provider_config.booking_status_mapping
                        "
                        ref="bookingStatusMapping"
                      />
                      <b-form-invalid-feedback
                        :state="
                          !v$.bookingPreset.config.provider_config.booking_status_mapping.$error
                        "
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.provider_config.booking_status_mapping"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 預約記錄頁籤 End -->
                    <!-- 清單列表欄位 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="清單列表欄位"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="bookingPreset.config.liff_field_setting.list"
                        ref="liffFieldSetting"
                      />
                      <b-form-invalid-feedback
                        :state="
                          !v$.bookingPreset.config.liff_field_setting.list
                            .$error
                        "
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.liff_field_setting.list"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 清單列表欄位 End -->
                    <!-- 明細頁狀態變更按鈕 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="明細頁狀態變更按鈕"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="bookingPreset.config.status_detector"
                        ref="statusDetector"
                      />
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.config.status_detector.$error"
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.status_detector"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 明細頁狀態變更按鈕 End -->
                    <!-- 明細頁通知按鈕 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="明細頁通知按鈕"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="bookingPreset.config.liff_detail_inform_button"
                        ref="liffDetailInformButton"
                      />
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.config.liff_detail_inform_button.$error"
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.liff_detail_inform_button"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 明細頁通知按鈕 End -->
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- liff預約紀錄 section end -->

              <!-- 預約設定 section start -->
              <section v-if="step == '#booking-setting'">
                <!-- 欄位部分 Start -->
                <div class="row d-flex mb-4 mb-xl-2">
                  <div class="col-12 col-xl-12">
                    <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    推播範本
                  </h4>
                  <div class="col-12 col-xl-8">
                    <validation-error-alert
                      v-if="validationErrors"
                      :errors="validationErrors"
                    ></validation-error-alert>
                    <!-- 填寫後通知 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="填寫後貼標"
                    >
                      <TagsBox
                        v-model="
                          bookingPreset.config.event_setting.booking_tags
                        "
                      ></TagsBox>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="表頭背景色"
                    >
                      <color-picker v-model="bookingPreset.config.push_template.header_bg_color"></color-picker>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="按鈕背景色"
                    >
                      <color-picker v-model="bookingPreset.config.push_template.button_bg_color"></color-picker>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    預約後推播
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <div class="d-flex flex-column">
                        <div>
                          <b-form-checkbox v-model="bookingPreset.config.created_push_message">
                            啟用預約後立即推播-客戶
                            <span id="booking-created-message-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-created-message-tooltip-trigger" placement="top">
                              使用 booking_created_message 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                        <div class="mt-2">
                          <b-form-checkbox v-model="bookingPreset.config.created_push_message_staff">
                            啟用預約後立即推播-員工
                            <span id="booking-created-message-staff-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-created-message-staff-tooltip-trigger" placement="top">
                              使用 booking_created_message_staff 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                      </div>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <template #label>
                        表頭圖片
                        <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                      </template>
                      <template #default>
                        <AppFileUploader
                          v-model="bookingPreset.config.created_push_header_image_url"
                          placeholder-when-empty=""
                        />
                      </template>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="推播文字"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.created_push_message_content"
                        rows="4"
                      ></b-form-textarea>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    確認後推播
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <div class="d-flex flex-column">
                        <div>
                          <b-form-checkbox v-model="bookingPreset.config.notify.confirmed.enabled">
                            啟用確認後推播-客戶
                            <span id="booking-confirmed-message-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-confirmed-message-tooltip-trigger" placement="top">
                              使用 booking_confirmed_message 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                        <div class="mt-2">
                          <b-form-checkbox v-model="bookingPreset.config.notify.confirmed.staff_enabled">
                            啟用確認後推播-員工
                            <span id="booking-confirmed-message-staff-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-confirmed-message-staff-tooltip-trigger" placement="top">
                              使用 booking_confirmed_message_staff 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                      </div>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <template #label>
                        表頭圖片
                        <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                      </template>
                      <template #default>
                        <AppFileUploader
                          v-model="bookingPreset.config.confirmed_push_header_image_url"
                          placeholder-when-empty=""
                        />
                      </template>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="推播文字"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.confirmed_push_message_content"
                        rows="4"
                      ></b-form-textarea>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="狀態定義"
                    >
                      <JsonEditor
                        height="64px"
                        v-model="bookingPreset.config.notify.confirmed.status_mapping"
                        ref="confirmedStatusMappingRef"
                      ></JsonEditor>
                      <b-form-invalid-feedback :state="!v$.bookingPreset.config.notify.confirmed.status_mapping.$error">
                        {{presentValidationErrors("bookingPreset.config.confirmed_push_message_content")}}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    完成後推播
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <div class="d-flex flex-column">
                        <div>
                          <b-form-checkbox v-model="bookingPreset.config.notify.completed.enabled">
                            啟用完成後推播-客戶
                            <span id="booking-completed-message-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-completed-message-tooltip-trigger" placement="top">
                              使用 booking_completed_message 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                        <div class="mt-2">
                          <b-form-checkbox v-model="bookingPreset.config.notify.completed.staff_enabled">
                            啟用完成後推播-員工
                            <span id="booking-completed-message-staff-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-completed-message-staff-tooltip-trigger" placement="top">
                              使用 booking_completed_message_staff 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                      </div>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <template #label>
                        表頭圖片
                        <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                      </template>
                      <template #default>
                        <AppFileUploader
                          v-model="bookingPreset.config.completed_push_header_image_url"
                          placeholder-when-empty=""
                        />
                      </template>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="推播文字"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.completed_push_message_content"
                        rows="4"
                      ></b-form-textarea>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="狀態定義"
                    >
                      <JsonEditor
                        height="64px"
                        v-model="bookingPreset.config.notify.completed.status_mapping"
                        ref="cancelNotifyRef"
                      ></JsonEditor>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    取消後推播
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <div class="d-flex flex-column">
                        <div>
                          <b-form-checkbox v-model="bookingPreset.config.notify.cancel.enabled">
                            啟用取消後推播-客戶
                            <span id="booking-canceled-message-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-canceled-message-tooltip-trigger" placement="top">
                              使用 booking_canceled_message 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                        <div class="mt-2">
                          <b-form-checkbox v-model="bookingPreset.config.notify.cancel.staff_enabled">
                            啟用取消後推播-員工
                            <span id="booking-canceled-message-staff-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-canceled-message-staff-tooltip-trigger" placement="top">
                              使用 booking_canceled_message_staff 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                      </div>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <template #label>
                        表頭圖片
                        <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                      </template>
                      <template #default>
                        <AppFileUploader
                          v-model="bookingPreset.config.canceled_push_header_image_url"
                          placeholder-when-empty=""
                        />
                      </template>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="推播文字"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.canceled_push_message_content"
                        rows="4"
                      ></b-form-textarea>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="狀態定義"
                    >
                      <JsonEditor
                        height="64px"
                        v-model="bookingPreset.config.notify.cancel.status_mapping"
                        ref="cancelNotifyRef"
                      ></JsonEditor>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    修改後推播
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <div class="d-flex flex-column">
                        <div>
                          <b-form-checkbox v-model="bookingPreset.config.notify.edit.enabled">
                            啟用修改後推播-客戶
                            <span id="booking-modified-message-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-modified-message-tooltip-trigger" placement="top">
                              使用 booking_modified_message 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                        <div class="mt-2">
                          <b-form-checkbox v-model="bookingPreset.config.notify.edit.staff_enabled">
                            啟用修改後推播-員工
                            <span id="booking-modified-message-staff-tooltip-trigger" class="ml-1 d-inline-flex align-items-center">
                              <span class="mdi mdi-help-circle-outline"></span>
                            </span>
                            <b-tooltip target="booking-modified-message-staff-tooltip-trigger" placement="top">
                              使用 booking_modified_message_staff 範本
                            </b-tooltip>
                          </b-form-checkbox>
                        </div>
                      </div>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <template #label>
                        表頭圖片
                        <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
                      </template>
                      <template #default>
                        <AppFileUploader
                          v-model="bookingPreset.config.modified_push_header_image_url"
                          placeholder-when-empty=""
                        />
                      </template>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="推播文字"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.modified_push_message_content"
                        rows="4"
                      ></b-form-textarea>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    每日提醒推播
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <div class="d-flex flex-column">
                        <b-form-checkbox v-model="bookingPreset.config.daily_booking_reminder_customer_enabled">
                          啟用每日提醒推播-客戶
                        </b-form-checkbox>
                        <b-form-checkbox class="mt-2" v-model="bookingPreset.config.daily_booking_reminder_staff_enabled">
                          啟用每日提醒推播-員工
                        </b-form-checkbox>
                      </div>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="推播設定"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="bookingPreset.config.daily_booking_reminder"
                        ref="dailyBookingReminder"
                      />
                      <b-form-invalid-feedback :state="!v$.bookingPreset.config.daily_booking_reminder.$error">
                        {{ presentValidationErrors('bookingPreset.config.daily_booking_reminder') }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 填寫後通知 End -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="推播文字"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.every_day_message"
                        rows="4"
                      ></b-form-textarea>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    後台推播設定
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                    >
                      <div class="d-flex flex-column">
                        <b-form-checkbox v-model="bookingPreset.config.status_change_push_customer_enabled">
                          啟用狀態變更後立即推播-客戶
                        </b-form-checkbox>
                        <b-form-checkbox class="mt-2" v-model="bookingPreset.config.status_change_push_staff_enabled">
                          啟用狀態變更後立即推播-員工
                        </b-form-checkbox>
                      </div>
                    </b-form-group>
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="狀態變更設定"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="bookingPreset.config.status_change_events"
                        ref="statusChangeEvent"
                      />
                      <b-form-invalid-feedback
                        :state="
                          !v$.bookingPreset.config.status_change_events.$error
                        "
                      >
                        {{
                          presentValidationErrors(
                            "bookingPreset.config.status_change_events"
                          )
                        }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- 預約設定 section end -->

              <!-- linked application section start -->
              <section v-if="step == '#linked-application'">
                <!-- 欄位部分 Start -->
                <div class="row d-flex mb-4 mb-xl-2">
                  <div class="col-12 col-xl-12">
                    <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                  </div>
                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    連動中應用
                  </h5>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    客戶員工關聯
                  </h4>
                  <div class="col-12 col-xl-8">
                    <validation-error-alert
                      v-if="validationErrors"
                      :errors="validationErrors"
                    ></validation-error-alert>
                    <!-- Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.customer_staff.enable"
                      >
                        建立客戶員工關聯
                      </b-form-checkbox>
                    </b-form-group>

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="關聯類型"
                    >
                      <b-form-input v-model="bookingPreset.config.customer_staff.bound_type"></b-form-input>
                    </b-form-group>
                  </div>
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    客戶分店關聯
                  </h4>
                  <div class="col-12 col-xl-8">
                    <validation-error-alert
                      v-if="validationErrors"
                      :errors="validationErrors"
                    ></validation-error-alert>
                    <!-- Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.customer_branches.enable"
                      >
                        建立客戶分店關聯
                      </b-form-checkbox>
                    </b-form-group>

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="關聯類型"
                    >
                      <b-form-input v-model="bookingPreset.config.customer_branches.bound_type"></b-form-input>
                    </b-form-group>
                  </div>

                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    聊天室
                  </h4>
                  <div class="col-12 col-xl-8">
                    <validation-error-alert
                      v-if="validationErrors"
                      :errors="validationErrors"
                    ></validation-error-alert>
                    <!-- Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.chatroom.enable"
                      >
                        啟用
                      </b-form-checkbox>
                    </b-form-group>

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="來源類型"
                    >
                      <b-form-select
                        v-model="bookingPreset.config.chatroom.source_type"
                      >
                        <b-form-select-option value="booking">預約</b-form-select-option>
                        <b-form-select-option value="customer_staff">客戶員工關聯</b-form-select-option>
                        <b-form-select-option value="customer_branches">客戶分店關聯</b-form-select-option>
                      </b-form-select>
                    </b-form-group>

                    <!-- Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.chatroom.is_auto"
                      >
                        預約後自動建立
                      </b-form-checkbox>
                    </b-form-group>

                    <!-- Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.chatroom.invite_booked_staff"
                      >
                        將被預約的員工加入聊天室
                      </b-form-checkbox>
                    </b-form-group>

                    <!-- Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                    >
                      <b-form-checkbox
                        v-model="bookingPreset.config.chatroom.invite_branches_staff"
                      >
                        邀請分店員工
                      </b-form-checkbox>
                    </b-form-group>

                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="請輸入分店員工角色"
                    >
                      <JsonEditor
                        height="128px"
                        v-model="bookingPreset.config.chatroom.invite_branches_staff_by_roles"
                      />
                    </b-form-group>
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- linked application section end -->

              <!-- 分頁與分區設定 section start -->
              <section v-if="step == '#pages-and-sections'">
                <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                <PageAndSectionEditor
                  editor-title="分頁與分區設定"
                  :inputSubjects="bookingPreset.config.fields"
                  :pages.sync="bookingPreset.config.pages"
                  :sections.sync="bookingPreset.config.sections"
                  expand-slot-area
                  @output="handlePageAndSectionUpdate"
                  @cancel="cancel"
                />
              </section>
              <!-- 分頁與分區設定 section end -->

              <!-- 欄位資訊 section start -->
              <section v-if="step == '#collection-info'">
                <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                <h4 class="font-weight-bold">欄位資訊</h4>
                <DynamicFormReadOnly
                  v-if="bookingPreset.id"
                  :input-subjects="bookingPreset.config.fields"
                  @cancel="$router.push({ name: 'BookingPresetList' })"
                ></DynamicFormReadOnly>
              </section>

              <!-- 估價單設定 section start -->
              <section
                v-if="
                  step == '#booking-list-quotation-setting' &&
                  checkPermission([consts.BOOKING_LIST_QUOTATION_SETTING])
                "
              >
                <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>

                <h4 class="font-weight-bold">估價單設定</h4>
                <div class="col-12 col-xl-12">
                  <validation-error-alert
                    v-if="validationErrors"
                    :errors="validationErrors"
                  ></validation-error-alert>
                  <!-- 表頭設定 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="表頭設定"
                  >
                    <b-form-textarea
                      class="mb-2 mr-sm-2 mb-sm-0"
                      v-model="bookingPreset.config.quotation_header"
                      rows="10"
                    ></b-form-textarea>
                  </b-form-group>
                  <!-- 表頭設定 End -->
                  <!-- 內容標題 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="內容標題"
                  >
                    <b-input v-model="bookingPreset.config.quotation_title" type="text" class="form-control" placeholder="請輸入內容標題" />
                  </b-form-group>
                  <!-- 內容標題 End -->
                  <!-- 內容副標題 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="內容副標題"
                  >
                    <b-input v-model="bookingPreset.config.quotation_subTitle" type="text" class="form-control" placeholder="請輸入內容副標題" />
                  </b-form-group>
                  <!-- 內容副標題 End -->
                  <!-- 內容表頭設定 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                  >
                    <span id="quotation-content-header-tooltip">
                      內容表頭設定
                      <span class="mdi mdi-information" />
                    </span>
                    <b-tooltip placement="top" target="quotation-content-header-tooltip">
                      <template #default>
                        <div style="text-align: left">
                          <span>優先出現field_id，對應的文字，如果不存在或為空，則出現text中的文字</span><br />
                          <span>{</span><br />
                          <span>"text": "純文字",</span><br />
                          <span>"style": "顯示的style",</span><br />
                          <span>"colspan": "所佔的格數，數字表示",</span><br />
                          <span>"field_id": "data的field_id，會轉換成對應的文字"</span><br />
                          <span>}</span><br />
                        </div>
                      </template>
                    </b-tooltip>
                    <JsonEditor
                      height="256px"
                      v-model="bookingPreset.config.quotation_content_header"
                      ref="quotationContentHeaderManagement"
                    />
                    <b-form-invalid-feedback :state="!v$.bookingPreset.config.quotation_content_header.$error">
                      {{ presentValidationErrors('bookingPreset.config.quotation_content_header') }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                  <!-- 內容表頭設定 End -->
                  <!-- 內容設定 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                  >
                    <span id="quotation-content-tooltip">
                      內容設定
                      <span class="mdi mdi-information" />
                    </span>
                    <b-tooltip placement="top" target="quotation-content-tooltip">
                      <template #default>
                        <div style="text-align: left">
                          <span>優先出現field_id，對應的文字，如果不存在或為空，則出現text中的文字</span><br />
                          <span>{</span><br />
                          <span>"text": "純文字",</span><br />
                          <span>"style": "顯示的style",</span><br />
                          <span>"colspan": "所佔的格數，數字表示",</span><br />
                          <span>"field_id": "data的field_id，會轉換成對應的文字"</span><br />
                          <span>}</span><br />
                        </div>
                      </template>
                    </b-tooltip>
                    <JsonEditor
                      height="256px"
                      v-model="bookingPreset.config.quotation_content"
                      ref="quotationContentManagement"
                    />
                    <b-form-invalid-feedback :state="!v$.bookingPreset.config.quotation_content.$error">
                      {{ presentValidationErrors('bookingPreset.config.quotation_content') }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                  <!-- 內容設定 End -->
                  <!-- 內容頁尾設定 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                  >
                    <span id="quotation-content-footer-tooltip">
                      內容頁尾設定
                      <span class="mdi mdi-information" />
                    </span>
                    <b-tooltip placement="top" target="quotation-content-footer-tooltip">
                      <template #default>
                        <div style="text-align: left">
                          <span>優先出現field_id，對應的文字，如果不存在或為空，則出現text中的文字</span><br />
                          <span>{</span><br />
                          <span>"text": "純文字",</span><br />
                          <span>"style": "顯示的style",</span><br />
                          <span>"colspan": "所佔的格數，數字表示",</span><br />
                          <span>"field_id": "data的field_id，會轉換成對應的文字"</span><br />
                          <span>}</span><br />
                        </div>
                      </template>
                    </b-tooltip>
                    <JsonEditor
                      height="256px"
                      v-model="bookingPreset.config.quotation_content_footer"
                      ref="quotationContentFooterManagement"
                    />
                    <b-form-invalid-feedback :state="!v$.bookingPreset.config.quotation_content_footer.$error">
                      {{ presentValidationErrors('bookingPreset.config.quotation_content_footer') }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                  <!-- 內容頁尾設定 End -->
                  <!-- 頁尾設定 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="頁尾設定"
                  >
                    <b-form-textarea
                      class="mb-2 mr-sm-2 mb-sm-0"
                      v-model="bookingPreset.config.quotation_footer"
                      rows="10"
                    ></b-form-textarea>
                  </b-form-group>
                  <!-- 頁尾設定 End -->
                  <!-- 發票買受人統一編號欄位 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="發票買受人統一編號欄位"
                  >
                    <b-form-select
                      v-model="bookingPreset.config.buyer_business_number_id"
                      :options="collectionFieldCandidatesWithEmptyString"
                    ></b-form-select>
                  </b-form-group>
                  <!-- 發票買受人統一編號欄位 End -->
                  <!-- 發票買受人公司名稱欄位 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="發票買受人公司名稱欄位"
                  >
                    <b-form-select
                      v-model="bookingPreset.config.buyer_company_name_id"
                      :options="collectionFieldCandidatesWithEmptyString"
                    ></b-form-select>
                  </b-form-group>
                  <!-- 發票買受人公司名稱欄位 End -->
                  <!-- 發票品名 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="發票品名"
                    description="開立發票時的品名，留空則使用系統預設"
                  >
                    <b-form-input
                      v-model="bookingPreset.config.invoice_item_name"
                      placeholder="車資"
                    ></b-form-input>
                  </b-form-group>
                  <!-- 發票品名 End -->
                  <!-- 發票開立日期 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="發票開立日期"
                    :description="`支援變數：{{$booking_data['StartDate']}}，留空則使用當天日期`"
                  >
                    <b-form-input
                      v-model="bookingPreset.config.invoice_issue_date"
                      :placeholder="`{{$booking_data['StartDate']}}`"
                    ></b-form-input>
                  </b-form-group>
                  <!-- 發票開立日期 End -->
                  <!-- 發票備註 Start -->
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="12"
                    label-size="sm"
                    label="發票備註"
                    :description="`支援變數：{{$booking_data['StartDate']}}、{{$booking_data['StartDateTime']}}、{{$booking_data['StartToEndDate']}} 等，留空則使用系統預設`"
                  >
                    <b-form-input
                      v-model="bookingPreset.config.invoice_remark"
                      :placeholder="`{{$booking_data['StartToEndDate']}} 用車`"
                    ></b-form-input>
                  </b-form-group>
                  <!-- 發票備註 End -->
                  <!-- 表單底部 Start -->
                  <div class="d-flex justify-content-center mt-2">
                    <b-button
                      class="mr-3"
                      @click="cancel"
                      variant="outline-danger"
                    >
                      返回
                    </b-button>
                    <b-button @click="submit" variant="success">
                      儲存
                    </b-button>
                  </div>
                  <!-- 表單底部 End -->
                </div>
              </section>

              <section v-if="step == '#limit-setting'">
                <div class="row d-flex mb-4 mb-xl-2">
                  <div class="col-12 col-xl-12">
                    <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>
                  </div>
                  <h4 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    預約次數限制
                  </h4>
                  <div class="col-12 col-xl-8">
                    <b-form-checkbox
                      v-model="bookingPreset.config.occupy_config.enable"
                      :disabled="!bookingPreset.config.booking_mapping.bookings.start_at"
                    >
                      預約次數限制
                    </b-form-checkbox>
                  </div>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold mt-2">
                    檢查條件
                  </h5>
                  <div class="col-12 col-xl-8">
                    <ul class="list-none pl-0 mb-3">
                      <li class="mb-2">
                        <b-form-checkbox
                          v-model="bookingPreset.config.occupy_config.enable"
                          :disabled="!bookingPreset.config.occupy_config.enable"
                        >預約時段</b-form-checkbox>
                      </li>
                      <li class="mb-2">
                        <b-form-checkbox
                          v-model="bookingPreset.config.occupy_config.is_check_collection"
                          :disabled="!bookingPreset.config.occupy_config.enable"
                        >
                          <b-form-select
                            v-model="bookingPreset.config.occupy_config.collection_id"
                            :options="collectionOptions"
                            :disabled="!bookingPreset.config.occupy_config.is_check_collection"
                          />
                        </b-form-checkbox>
                      </li>
                      <li class="mb-2">
                        <b-form-checkbox
                          v-model="bookingPreset.config.occupy_config.is_check_branch"
                          :disabled="!bookingPreset.config.occupy_config.enable"
                        >分店</b-form-checkbox>
                      </li>
                      <li class="mb-2">
                        <b-form-checkbox
                          v-model="bookingPreset.config.occupy_config.is_check_staff"
                          :disabled="!bookingPreset.config.occupy_config.enable || bookingPreset.type !== 'wizard'"
                        >員工</b-form-checkbox>
                      </li>
                      <li>
                        <b-form-checkbox
                          v-model="bookingPreset.config.occupy_config.is_check_service"
                          :disabled="!bookingPreset.config.occupy_config.enable || bookingPreset.type !== 'wizard'"
                        >服務項目</b-form-checkbox>
                      </li>
                    </ul>
                  </div>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    名額佔用規則
                  </h5>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="填入佔用名額的預約狀態"
                    >
                      <JsonEditor
                        height="32px"
                        v-model="bookingPreset.config.occupy_config.occupy_status"
                        :disabled="!bookingPreset.config.occupy_config.enable"
                      />
                    </b-form-group>
                  </div>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    次數上限
                  </h5>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label=""
                    >
                      <b-form-radio-group v-model="bookingPreset.config.occupy_config.limit_type" class="">
                        <b-form-radio value="collection" class="form-inline mb-2 col-12" :disabled="!bookingPreset.config.occupy_config.is_check_collection">
                          <b-form-select
                            v-model="bookingPreset.config.occupy_config.collection_id"
                            :options="collectionOptions"
                            :disabled="true"
                          />
                          <b-form-select
                            class="ml-2"
                            v-model="bookingPreset.config.occupy_config.collection_field_id"
                            :options="collectionItemOptions(bookingPreset.config.occupy_config.collection_id)"
                            :disabled="!(bookingPreset.config.occupy_config.limit_type == 'collection')"
                          />
                        </b-form-radio>
                        <b-form-radio value="time" class="form-inline mb-2 col-12" :disabled="!limitTimeField">
                          依時段設定
                          時段限定
                          <input
                            class="mb-2 mr-sm-2 mb-sm-0 w-4"
                            type="text"
                            v-model="bookingPreset.config.occupy_config.slot_people"
                          /> 人
                        </b-form-radio>
                      </b-form-radio-group>
                    </b-form-group>
                  </div>

                  <h5 class="col-12 col-xl-12 mb-3 font-weight-bold">
                    前台用戶顯示設定
                  </h5>
                  <div class="col-12 col-xl-8">
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="3"
                      label-size="sm"
                      label="額滿提示文字"
                    >
                      <b-form-input
                        v-model="bookingPreset.config.occupy_config.full_alert_text"
                        placeholder="請填入預約額滿的提示訊息"
                      />
                    </b-form-group>
                  </div>
                </div>
                <div class="d-flex justify-content-center mt-2">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
              </section>
            </b-card>
          </b-overlay>
        </div>
      </div>
    </div>
    <!-- Form End -->
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import {required, helpers, requiredIf} from "@vuelidate/validators";
import bookingApi from "@/apis/booking";
import AppFileUploader from "@/components/AppFileUploader.vue";
import JsonEditor from "@/components/Dashboard/JsonEditor.vue";
import StepNavigation from "@/components/Dashboard/StepNavigation.vue";
import DynamicFormEditor from "@/components/DynamicFormEditor/DynamicFormEditor.vue";
import DynamicFormReadOnly from "@/components/DynamicFormEditor/DynamicFormReadOnly.vue";
import PageAndSectionEditor from "@/components/DynamicFormEditor/PageAndSectionEditor.vue";
import TagsBox from "@/components/TagsBox";
import TrixEditor from "@/components/TrixEditor";
import mergeDefaultAndDataModel from "@/utils/mergeDefaultAndDataModel";
import deepGet from "lodash/get";
import { invoke } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { mapGetters } from "vuex";
import collectionApi from "@/apis/collection";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import NameBlock from "@/pages/Dashboard/Share/NameBlock.vue";
import AppTimePicker from '@/components/AppTimePicker.vue';
import AppDateTimePicker from '@/components/AppDateTimePicker.vue';
import { isBefore } from "date-fns";
import Datepicker from "vuejs-datepicker";
import VueTimepicker from "vue2-timepicker";
import ColorPicker from "@/components/ColorPicker.vue";
import BookingPreset from '@/models/BookingPreset.model';
import DynamicFormField from '@/models/DynamicFormField.model';
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    'color-picker': ColorPicker,
    AppTimePicker,
    AppDateTimePicker,
    AppFileUploader,
    JsonEditor,
    StepNavigation,
    DynamicFormEditor,
    DynamicFormReadOnly,
    PageAndSectionEditor,
    TagsBox,
    NameBlock,
    TrixEditor,
    Datepicker,
    VueTimepicker,
    BreadCrumb
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      bookingPreset: {
        name: { required },
        type: { required },
        start_at: {
          required,
          beforeEndAt: () => {
            return !this.bookingPreset.start_at ||
              !this.bookingPreset.end_at ||
              isBefore(new Date(this.bookingPreset.start_at), new Date(this.bookingPreset.end_at))
          },
        },
        end_at: {
          required,
        },
        config: {
          daily_booking_reminder: !(this.isEditing && this.step === '#booking-setting') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => invoke(vm, '$refs.dailyBookingReminder.isValidArray') || false,
            ),
          },
          auto_change_status_date_field_id: {
            required: helpers.withMessage(
              '此欄位必填',
              requiredIf(() => {
                return this.step === '#mapping' && this.bookingPreset.config.auto_change_status_enabled
              })
            )
          },
          provider_config: {
            status_filter_mapping: !(this.isEditing && this.step === "#mapping")
              ? {}
              : {
                "array": helpers.withMessage(
                  "必須為 Array 格式",
                  (value, vm) => vm.$refs.statusFilterMapping?.isValidArray() || false,
                ),
              },
            booking_status_mapping: !(this.isEditing && this.step === "#liff-setting")
              ? {}
              : {
                "array": helpers.withMessage(
                  "必須為 Array 格式",
                  (value, vm) => vm.$refs.bookingStatusMapping?.isValidArray() || false,
                ),
              },
          },
          liff_field_setting: {
            list: !(this.isEditing && this.step === '#liff-setting') ? {} : {
              "array": helpers.withMessage(
                "必須為 Array 格式",
                (value, vm) => invoke(vm, '$refs.liffFieldSetting.isValidArray') || false,
              ),
            },
          },
          status_change_events: !(this.isEditing && this.step === '#booking-setting') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => vm.$refs.statusChangeEvent?.isValidArray() || false,
            ),
          },
          status_detector: !(this.isEditing && this.step === '#liff-setting') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => vm.$refs.statusDetector?.isValidArray() || false,
            ),
          },
          liff_detail_inform_button: !(this.isEditing && this.step === '#liff-setting') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => vm.$refs.liffDetailInformButton?.isValidArray() || false,
            ),
          },
          quotation_content_header: !(this.isEditing && this.step === '#booking-list-quotation-setting') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => invoke(vm, '$refs.quotationContentHeaderManagement.isValidArray') || false,
            ),
          },
          quotation_content: !(this.isEditing && this.step === '#booking-list-quotation-setting') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => invoke(vm, '$refs.quotationContentManagement.isValidArray') || false,
            ),
          },
          quotation_content_footer: !(this.isEditing && this.step === '#booking-list-quotation-setting') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => invoke(vm, '$refs.quotationContentFooterManagement.isValidArray') || false,
            ),
          },
          can_edit_at_liff_mapping: !(this.isEditing && this.step === '#mapping') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => invoke(vm, '$refs.editLiffMapping.isValidArray') || false,
            ),
          },
          auto_change_status_overdue: !(this.isEditing && this.step === '#mapping') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => invoke(vm, '$refs.autoChangeStatusOverdue.isValidArray') || false,
            ),
          },
          auto_change_status_equal: !(this.isEditing && this.step === '#mapping') ? {} : {
            "array": helpers.withMessage(
              "必須為 Array 格式",
              (value, vm) => invoke(vm, '$refs.autoChangeStatusEqual.isValidArray') || false,
            ),
          },
          booking_mapping: {
            bookings: {
              end_slot_minutes: {
                isPositiveInteger: helpers.withMessage(
                  '預留時間區段(分鐘)，必須為正整數',
                  function (value) {
                    if (!this.isEditing || this.step !== '#mapping') {
                      return true; // 略過驗證
                    }
                    const n = Number(value);
                    return Number.isInteger(n) && n >= 0;
                  }
                )
              },
              closing_time: {
                required: helpers.withMessage('當日休店時間為必填', value => {
                  if (!this.isEditing || this.step !== '#mapping') {
                    return true; // 略過驗證
                  }
                  return value instanceof Set ? value.size > 0 : !!value;
                })
              },
              title_template: {}
            }
          },
          service: {
            title_item_id: !(this.isEditing && this.step === '#wizard-mode' && this.bookingPreset.config.service.collection_id) ? {} : {
              required
            }
          },
          notify: {
            confirmed: {
              status_mapping: !(this.isEditing && this.step === '#booking-setting') ? {} : {
                "array": helpers.withMessage(
                  "必須為 Array 格式",
                  (value, vm) => vm.$refs.confirmedStatusMappingRef?.isValidArray() || false,
                ),
              },
            }
          },
          basic: {
            lead_time: {
              amount_day: {
                isPositiveInteger: helpers.withMessage(
                  '提前預約設定輸入欄位必須為正整數',
                  function (value, vm) {
                    if (vm.bookingPreset.config.basic.lead_time.type !== 'day') return true;
                    const n = Number(value);
                    return Number.isInteger(n) && n > 0;
                  }
                )
              },
              amount_minute: {
                isPositiveInteger: helpers.withMessage(
                  '提前預約設定輸入欄位必須為正整數',
                  function (value, vm) {
                    if (vm.bookingPreset.config.basic.lead_time.type !== 'minute') return true;
                    const n = Number(value);
                    return Number.isInteger(n) && n > 0;
                  }
                )
              },
              lessThanBookingWindow: helpers.withMessage(
                '提前預約設定不得超過預約時間限制',
                function (value, vm) {
                  return (new BookingPreset(vm.bookingPreset)).bookingWindowIsGreaterThanLeadTime()
                }
              )
            }
          },
        },
      },
    };
  },
  computed: {
    consts() {
      return consts;
    },
    ...mapGetters("route", ["routeQuery"]),
    navigationSteps() {
      return [
        {
          title: "基本設定",
          hash: "",
          has_checkbox: true,
          is_checked: this.bookingPreset.id,
          is_disabled: false,
        },
        {
          title: "歡迎頁",
          hash: "#welcome",
          has_checkbox: true,
          is_checked: deepGet(this.bookingPreset, 'config.welcome_page.enable', false),
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "內容頁",
          hash: "#content",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "完成頁",
          hash: "#finished",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "欄位設定",
          hash: "#fields",
          has_checkbox: true,
          is_checked: deepGet(this.bookingPreset, 'config.fields.length', 0) > 0,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "欄位資訊",
          hash: "#collection-info",
          has_checkbox: true,
          is_checked: deepGet(this.bookingPreset, 'config.fields.length', 0) > 0,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "基本預約設定",
          hash: "#mapping",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "精靈模式設定",
          hash: "#wizard-mode",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
          hidden: this.bookingPreset.type !== 'wizard',
        },
        {
          title: "精靈時間選擇頁",
          hash: "#wizard-page",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "LIFF 預約紀錄",
          hash: "#liff-setting",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "推播設定",
          hash: "#booking-setting",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "連動中應用",
          hash: "#linked-application",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "分頁與分區設定",
          hash: "#pages-and-sections",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "估價單設定",
          hash: "#booking-list-quotation-setting",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
          hidden: !this.checkPermission([consts.BOOKING_LIST_QUOTATION_SETTING]),
        },
        {
          title: "預約次數限制",
          hash: "#limit-setting",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
      ];
    },
    dataModel() {
      return new BookingPreset(this.bookingPreset)
    },
    limitTimeField() {
      const startAtFieldId = deepGet(this, "bookingPreset.config.booking_mapping.bookings.start_at", null)
      const timeField = deepGet(this, "bookingPreset.config.fields", []).find((field) => field._id == startAtFieldId);
      return timeField
    },
    dateFieldCandidates() {
      return [
        { text: "未設定", value: null },
        ...this.bookingPreset.config.fields.filter((field) => {
          return (field.type == 'datetime') && ['datetime', 'date'].includes(field.format)
        }).map((field) => ({
          text: deepGet(field, "config.title") || deepGet(field, "title"),
          value: deepGet(field, "config._id") || deepGet(field, "_id"),
        })),
      ];
    },
    textFieldCandidates() {
      return [
        { text: "未設定", value: null },
        ...this.bookingPreset.config.fields.filter((field) => {
          return field.type === 'text'
        }).map((field) => ({
          text: deepGet(field, "config.title") || deepGet(field, "title"),
          value: deepGet(field, "config._id") || deepGet(field, "_id"),
        })),
      ];
    },
    branchFieldCandidates() {
      return [
        { text: "未設定", value: null },
        ...this.bookingPreset.config.fields.filter((field) => {
          return field.type == 'shop_selection'
        }).map((field) => ({
          text: deepGet(field, "config.title") || deepGet(field, "title"),
          value: deepGet(field, "config._id") || deepGet(field, "_id"),
        })),
      ];
    },
    serviceFieldCandidates() {
      return [
        { text: "未設定", value: null },
        ...this.bookingPreset.config.fields.filter((field) => {
          return ['collection_carousel', 'collection_select'].includes(field.type)
        }).map((field) => ({
          text: deepGet(field, "config.title") || deepGet(field, "title"),
          value: deepGet(field, "config._id") || deepGet(field, "_id"),
          collection_id: deepGet(field, "config.collection_id") || deepGet(field, "collection_id"),
        })),
      ];
    },
    collectionFieldCandidates() {
      return [
        { text: "未設定", value: null },
        ...this.bookingPreset.config.fields.map((field) => ({
          text: deepGet(field, "config.title") || deepGet(field, "title"),
          value: deepGet(field, "config._id") || deepGet(field, "_id"),
        })),
      ];
    },
    collectionFieldCandidatesWithEmptyString() {
      return [
        { text: "未設定", value: "" },
        ...this.bookingPreset.config.fields.map((field) => ({
          text: deepGet(field, "config.title") || deepGet(field, "title"),
          value: deepGet(field, "config._id") || deepGet(field, "_id"),
        })),
      ];
    },
    titleOptions() {
      const bookingNumberOption = {
        text: "預約編號",
        value: "booking_number",
      }

      if (this.bookingPreset.type == "wizard") {
        return [
          ...this.collectionFieldCandidates,
          {
            text: "分店",
            value: "branch_title",
          },
          {
            text: "員工",
            value: "staff_title",
          },
          {
            text: "服務",
            value: "service_title",
          },
          bookingNumberOption,
        ];
      }

      return [...this.collectionFieldCandidates, bookingNumberOption];
    },
    permissionOptions() {
      return [
        { text: "需註冊", value: "registered" },
      ]
    },
    orderOptions() {
      return [
        {
          text: "第一",
          value: 1,
        },
        {
          text: "第二",
          value: 2,
        },
        {
          text: "第三",
          value: 3,
        },
        {
          text: "第四",
          value: 4,
        },
        {
          text: "第五",
          value: 5,
        },
      ]
    },
    bookingPresetTypes() {
      const availableTypes = []
      const permissionChecker = new PermissionChecker()

      if (permissionChecker.check([consts.BOOKING_NORMAL_MODE])) {
        availableTypes.push({ text: "一般模式", value: "normal" })
      }
      if (permissionChecker.check([consts.BOOKING_WIZARD_MODE])) {
        availableTypes.push({ text: "精靈模式", value: "wizard" })
      }
      return availableTypes
    },
    hasBookingPresetTypePermission() {
      return this.bookingPresetTypes.length > 0
    },
  },
  data() {
    return {
      isEditing: false,
      showLoading: false,
      validationErrors: null,
      step: "",
      bookingPreset: { ...BookingPreset.defaultValue() },
      collectionOptions: [],
      validSubject: false,
      permissionChecker: new PermissionChecker(),
    };
  },
  mounted() {
    if (this.$route.name === "BookingPresetEdit") {
      this.isEditing = true;
      this.bookingPreset.id = this.$route.params.id;
      this.fetchBookingPreset();
      this.$refs.stepNavigation.navigateToCurrentRoute();
    } else {
      this.isEditing = false;
      this.$set(
        this,
        "bookingPreset",
        this.mergeDefaultAndDataModel(this.bookingPreset, null)
      );
      // 權限只有一個時自動選取
      if (this.bookingPresetTypes.length === 1) {
        this.bookingPreset.type = this.bookingPresetTypes[0].value;
      }
    }
    this.fetchCollections();
  },
  watch: {
    'bookingPreset.type'(newType) {
      if (newType === 'normal') {
        this.bookingPreset.config.occupy_config.is_check_service = false;
        this.bookingPreset.config.occupy_config.is_check_staff = false;
      }
    }
  },
  methods: {
    deepGet,
    mergeDefaultAndDataModel,
    handleFieldChange(subject, field) {
      if (this.bookingPreset.type === 'wizard' && field === 'format') {
        this.validateBookingTimeFormatInWizardMode(subject)
      }
    },
    validateBookingTimeFormatInWizardMode(subject) {
      const startAtFieldId = this.dataModel.startAtFieldId;
      if (!startAtFieldId) return;

      if (subject._id === startAtFieldId && subject.type === 'datetime' && subject.format === 'time') {
        this.$swal("提醒", "精靈模式的預約時間類型請選擇 日期 或 日期+時間", "warning");
        this.$nextTick(() => {
          const fieldIndex = DynamicFormField
            .fromArray(this.bookingPreset.config.fields)
            .findIndex(fieldModel => fieldModel._id === subject._id)
          if (fieldIndex !== -1) {
            this.$set(this.bookingPreset.config.fields[fieldIndex], 'format', 'datetime');
          }
        });
      }
    },
    changeService(fieldId) {
      this.bookingPreset.config.service.image_item_id = null;
      this.bookingPreset.config.service.description_item_id = null;
      this.bookingPreset.config.service.title_item_id = null;
      this.bookingPreset.config.service.price_item_id = null;
      this.bookingPreset.config.service.duration_item_id = null;
      if (!fieldId) {
        this.bookingPreset.config.service.collection_id = null;
        this.bookingPreset.config.service.service_field_id = null;
        return;
      }

      const selectedField = this.serviceFieldCandidates.find(candidate => candidate.value === fieldId);
      if (!selectedField) {
        return;
      }

      if (selectedField) {
        this.bookingPreset.config.service.service_field_id = fieldId;
        this.bookingPreset.config.service.collection_id = selectedField.collection_id;
      }
    },
    collectionItemOptions(collectionId) {
      return [
        { text: "未設定", value: null },
        ...this.collectionOptions
          .find(
            (collection) =>
              collection.value == collectionId
          )
          ?.config?.fields.map((field) => {
            return {
              value: field._id ?? field.config._id,
              text: field.title ?? field.config.title,
            };
          }) ?? []
      ];
    },
    async fetchCollections() {
      try {
        let response = await collectionApi.getCollections({
          with_items: 1,
          is_enabled: 1,
        });
        this.collectionOptions = response.data.data.map((collection) => {
          return {
            value: collection.id,
            text: collection.name + " 共 " + collection.items_count + " 筆資料",
            items: collection.items,
            config: collection.config,
          };
        });
      } catch (error) {
        console.error(error);
      }
    },
    presentValidationErrors(validationKey) {
      return deepGet(this.v$, validationKey + ".$errors", [])
        .map((error) => error.$message)
        .join(", ");
    },
    cancel() {
      this.$router.push({ name: "BookingPresetList", query: this.routeQuery });
    },
    async submit() {
      const result = await this.v$.$validate();
      if (!result) {
        if (this.step !== '') {

          if (['amount_minute', 'amount_day'].includes(this.v$.$errors[0].$property) || this.v$.$errors[0].$validator === 'lessThanBookingWindow') {
            this.$swal({
              type: 'error',
              title: '欄位填寫有誤',
              html: this.v$.$errors
                .map(error => `${error.$message}`)
                .join('<br/>'),
              confirmButtonText: '確認',
            })
          } else {
            this.$swal({
              type: 'error',
              title: '欄位填寫有誤',
              html: this.v$.$errors
                .map(error => `${error.$propertyPath}: ${error.$message}`)
                .join('<br/>'),
              confirmButtonText: '確認',
            })
          }
        }
        return false;
      }

      try {
        this.dataModel.validateNormalConfiguration()
      } catch (error) {
        this.$swal({
          type: 'error',
          title: '設定有誤',
          html: error.messages.join('<br/>'),
          confirmButtonText: '確認',
        })
        return false
      }

      if (this.step === '#booking-setting') {
        // 表頭背景色
        const headerBgColor  = this.bookingPreset.config.push_template.header_bg_color;
        const colorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/; // 檢查 #RRGGBB 或 #RGB 格式
        if (headerBgColor !== null) {
          if (!colorRegex.test(headerBgColor)) {
            this.$swal({
              type: 'error',
              title: '表頭背景色格式不正確',
            });
            return false;
          }
        }
        // 按鈕背景色
        const buttonBgColor  = this.bookingPreset.config.push_template.button_bg_color;
        if (buttonBgColor !== null) {
          if (!colorRegex.test(buttonBgColor)) {
            this.$swal({
              type: 'error',
              title: '按鈕背景色格式不正確',
            });
            return false;
          }
        }
      }

      try {
        this.showLoading = true;

        const payload = this.bookingPreset;
        if (payload?.config?.occupy_config && "time_options" in payload.config.occupy_config) {
          delete payload.config.occupy_config.time_options;
        }

        // 編輯表單
        if (this.isEditing) {
          await bookingApi.updateBookingPreset(
            this.bookingPreset.id,
            payload
          );
          await this.fetchBookingPreset();
          this.$swal("儲存成功", "", "success");
        }
        // 新增表單
        else {
          // const { pages, sections } = this.createDefaultPageAndSections();
          this.$set(this.bookingPreset.config, "pages", []);
          this.$set(this.bookingPreset.config, "sections", []);
          let response = await bookingApi.createBookingPreset(payload);
          this.bookingPreset = response.data.data;
          this.$swal("新增成功", "", "success");
          this.isEditing = true;
          this.$router.replace({
            name: "BookingPresetEdit",
            params: { id: response.data.data.id },
          });
        }

        return true;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存失敗", "error");
        return false;
      } finally {
        this.showLoading = false;
      }
    },
    async fetchBookingPreset() {
      if (!this.bookingPreset.id) {
        this.$swal("錯誤", "無法取得讀取 ID", "error");
        return;
      }

      try {
        this.showLoading = true;
        const response = await bookingApi.getBookingPreset(this.bookingPreset.id);

        this.$set(
          this,
          "bookingPreset",
          this.mergeDefaultAndDataModel({ ...BookingPreset.defaultValue() }, response.data.data)
        );
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取資料錯誤", "error");
        this.cancel();
      } finally {
        this.showLoading = false;
      }
    },
    async saveFields(fields) {
      this.validSubject = true

      if (fields.some(subject => !subject.title)) {
        return;
      }

      try {
        this.showLoading = true;

        this.bookingPreset.config.fields = fields;

        await bookingApi.updateBookingPreset(
          this.bookingPreset.id,
          this.bookingPreset
        );
        await this.fetchBookingPreset();
        this.$swal("儲存成功", "", "success");
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存資料錯誤", "error");
      } finally {
        this.showLoading = false;
      }
    },
    createDefaultPageAndSections() {
      const pages = Array.from(Array(3)).map((empty, index) => {
        return {
          _id: uuidv4(),
          order: index + 1,
          title: `第${index + 1}頁`,
        };
      });

      const sections = pages.reduce((carry, page) => {
        return carry.concat(
          Array.from(Array(2)).map((empty, index) => {
            return {
              _id: uuidv4(),
              order: index + 1,
              title: `第${page.order}-${index + 1}區`,
              page_id: page._id,
              visible_when_any: [],
            };
          })
        );
      }, []);

      return { pages, sections };
    },
    checkPermission(permissions) {
      return this.permissionChecker.check(permissions);
    },
    handlePageAndSectionUpdate() {
      this.submit()
    },
    sanitizeEndSlotMinutes() {
      this.$nextTick(() => {
        let val = Number(this.bookingPreset.config.booking_mapping.bookings.end_slot_minutes);

        // 如果是空、不是數字或小於 0，就強制設為 0
        if (!this.bookingPreset.config.booking_mapping.bookings.end_slot_minutes?.toString().trim() ||
          isNaN(val) || val < 0) {
          this.bookingPreset.config.booking_mapping.bookings.end_slot_minutes = 0;
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.list-none {
  list-style-type: none;
}
</style>
